/**
 * /ndt-inspection/sources — the radiation source register
 * (GET /ndt-inspection/sources).
 *
 * CURRENT ACTIVITY IS THE API'S. It is decayed from the isotope half-life and
 * the reference activity/day to the tenant's today, server-side, and arrives in
 * the tenant's activity unit (`meta.units.activity`, GBq or Ci). This screen
 * prints it; it never computes a decay.
 *
 * Leak-test standing and custody (who holds the source now) are the API's too.
 * Status and isotope filter server-side; the counts tally the unfiltered,
 * unpaginated list.
 */
import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, LogOut, Plus, Radiation } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useNdtCatalog, useNdtSources } from '../../hooks/useNdtInspection';
import { queryLoadFailure } from '../../lib/load-failure';
import { canAdminNdt, formatNdtActivity, ndtLeakTestSkin, ndtSourceStatusSkin, NDT_SOURCE_STATUS_SKIN } from '../../lib/ndt-inspection';
import { NDT_SOURCE_STATUSES, type NdtSource } from '../../types/ndt-inspection';
import { SourceDialog } from './NdtDialogs';
import { NdtBadge, NdtChip, NdtChipRow, NdtEmpty, NdtPanel, NdtRegister, ndtDay, type NdtColumn } from './NdtBits';

const isStatus = (v: string | null): v is (typeof NDT_SOURCE_STATUSES)[number] =>
  !!v && (NDT_SOURCE_STATUSES as readonly string[]).includes(v);

/**
 * Who holds it: the holder's name when checked out, else "In store" — except a
 * source down a well (lost or abandoned in hole, api#1335), which is in nobody's
 * store and reads its status instead.
 */
const IN_HOLE = new Set(['lost_in_hole', 'abandoned_in_hole']);
export const custodyText = (s: NdtSource) =>
  s.custody.checkedOut ? s.custody.holderName || '—' : IN_HOLE.has(s.status) ? ndtSourceStatusSkin(s.status).label : 'In store';

export default function SourcesScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusParam = searchParams.get('status');
  const status = isStatus(statusParam) ? statusParam : '';
  const isotope = searchParams.get('isotope') ?? '';
  const [adding, setAdding] = useState(false);

  const params = useMemo(() => ({ status, isotope }), [status, isotope]);
  const filtered = !!(status || isotope);
  const listQuery = useNdtSources(params);
  const allQuery = useNdtSources();
  const catalog = useNdtCatalog();
  const sources = listQuery.data?.data ?? [];
  const units = listQuery.data?.meta.units ?? allQuery.data?.meta.units ?? null;
  const all = allQuery.isLoading || queryLoadFailure(allQuery) ? undefined : allQuery.data?.data;
  const countOf = (pick: (s: NdtSource) => boolean) => displayValue(all ? all.filter(pick).length : undefined);
  const countsLoading = allQuery.isLoading;
  const canAdd = canAdminNdt(user?.role) && !!units;

  const setParam = (key: string, next: string) => {
    const p = new URLSearchParams(searchParams);
    if (next) p.set(key, next);
    else p.delete(key);
    setSearchParams(p, { replace: true });
  };

  const columns: NdtColumn<NdtSource>[] = [
    {
      key: 'serial',
      header: 'Isotope / serial',
      render: (s) => (
        <>
          <p className="font-extrabold text-white">{s.isotopeLabel || s.isotope}</p>
          <p className="mt-0.5 font-mono text-xs text-fieldiq-muted">{s.serialNumber || '—'}</p>
        </>
      ),
    },
    {
      key: 'activity',
      header: 'Current activity',
      align: 'right',
      render: (s) => (
        <>
          {formatNdtActivity(s.currentActivity, units?.activity)}
          <p className="mt-0.5 text-xs font-sans text-fieldiq-muted">as of {ndtDay(s.currentActivityOn)}</p>
        </>
      ),
    },
    { key: 'device', header: 'Device', render: (s) => s.deviceName || '—' },
    { key: 'custody', header: 'Custody', render: (s) => custodyText(s) },
    {
      key: 'leak',
      header: 'Leak test due',
      render: (s) => (
        <>
          {ndtDay(s.leakTest.dueOn)}
          <div className="mt-1"><NdtBadge skin={ndtLeakTestSkin(s.leakTest.status)} value={s.leakTest.status} /></div>
        </>
      ),
    },
    { key: 'status', header: 'Status', render: (s) => <NdtBadge skin={ndtSourceStatusSkin(s.status)} value={s.status} /> },
  ];

  return (
    <div data-testid="ndt-sources-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="NDT inspection"
        title="Sources & Radiation"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canAdd ? (
            <button type="button" onClick={() => setAdding(true)} className="fi-action fi-action--standard fi-action--primary">
              <Plus className="size-4" aria-hidden="true" />
              Add source
            </button>
          ) : undefined
        }
        stats={[
          { icon: <Radiation className="size-4" aria-hidden="true" />, label: 'Sources', sub: 'Registered', tone: 'neutral', loading: countsLoading, value: countOf(() => true) },
          { icon: <LogOut className="size-4" aria-hidden="true" />, label: 'Out', sub: 'Checked out', tone: 'info', loading: countsLoading, value: countOf((s) => s.custody.checkedOut) },
          { icon: <AlertTriangle className="size-4" aria-hidden="true" />, label: 'Leak test', sub: 'Due, overdue or failed', tone: 'accent', loading: countsLoading, value: countOf((s) => s.leakTest.status === 'due_soon' || s.leakTest.status === 'expired' || s.leakTest.status === 'failed') },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <NdtChipRow label="Filter by status">
            <NdtChip label="All" active={!status} onClick={() => setParam('status', '')} count={countsLoading ? undefined : countOf(() => true)} />
            {NDT_SOURCE_STATUSES.map((s) => (
              <NdtChip key={s} label={NDT_SOURCE_STATUS_SKIN[s].label} active={status === s} onClick={() => setParam('status', s)} count={countsLoading ? undefined : countOf((x) => x.status === s)} />
            ))}
          </NdtChipRow>
          <NdtChipRow label="Filter by isotope">
            <NdtChip label="All isotopes" active={!isotope} onClick={() => setParam('isotope', '')} />
            {(catalog.data?.data?.isotopes ?? []).map((i) => (
              <NdtChip key={i.value} label={i.label} active={isotope === i.value} onClick={() => setParam('isotope', i.value)} count={countsLoading ? undefined : countOf((x) => x.isotope === i.value)} />
            ))}
          </NdtChipRow>

          <NdtPanel
            eyebrow="Source register"
            title="Sealed sources"
            testId="ndt-source-register"
            footer={
              !listQuery.isLoading && !queryLoadFailure(listQuery) && sources.length > 0
                ? filtered
                  ? `Showing ${sources.length} of ${countOf(() => true)} sources`
                  : `${sources.length} sources`
                : undefined
            }
          >
            <NdtRegister
              query={listQuery}
              rows={sources}
              columns={columns}
              rail={(s) => ndtSourceStatusSkin(s.status).rail}
              onOpen={(s) => navigate(`/ndt-inspection/sources/${s.id}`)}
              subject="radiation sources"
              testId="ndt-source"
              minWidth="min-w-[960px]"
              empty={filtered ? <NdtEmpty>No source matches these filters.</NdtEmpty> : <NdtEmpty>No radiation sources are registered.</NdtEmpty>}
              card={(s) => (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-extrabold text-white">{s.isotopeLabel || s.isotope}</p>
                      <p className="mt-0.5 font-mono text-xs text-fieldiq-muted">{s.serialNumber || '—'}</p>
                    </div>
                    <NdtBadge skin={ndtSourceStatusSkin(s.status)} value={s.status} />
                  </div>
                  <p className="mt-2 text-xs text-fieldiq-text">
                    {formatNdtActivity(s.currentActivity, units?.activity)} · {custodyText(s)}
                  </p>
                </>
              )}
            />
          </NdtPanel>
        </div>
      </section>

      {canAdd && (
        <SourceDialog open={adding} onClose={() => setAdding(false)} units={units} onCreated={(id) => navigate(`/ndt-inspection/sources/${id}`)} />
      )}
    </div>
  );
}
